import React, { useEffect, useState } from "react";
import { HeartHandshake, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { useLang } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

export default function Compatibility() {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const [types, setTypes] = useState([]);
  const [a, setA] = useState("");
  const [b, setB] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const suffix = lang === "hi" ? "Hi" : lang === "ta" ? "Ta" : "";
  const pick = (o, base) => (o && (o[base + suffix] || o[base])) || "";

  useEffect(() => {
    api.get("/api/archetypes").then((r) => setTypes(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!user) return;
    api.get("/api/results/my").then((r) => {
      const key = r.data?.[0]?.archetypeKey;
      if (key) setA((cur) => cur || key);
    }).catch(() => {});
  }, [user]);

  const compare = async () => {
    if (!a || !b) return;
    setBusy(true);
    try {
      const { data } = await api.get("/api/compatibility", { params: { a, b, lang } });
      setResult(data);
    } finally {
      setBusy(false);
    }
  };

  const TypeChip = ({ t: arch }) => arch ? (
    <span className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-xs font-semibold">
      <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: arch.color }} />
      {pick(arch, "name")}
    </span>
  ) : null;

  return (
    <div className="mx-auto max-w-3xl px-4 py-14" data-testid="compatibility-page">
      <h1 className="font-display text-4xl font-semibold sm:text-5xl" data-testid="compat-title">{t("compat.title")}</h1>
      <p className="mt-3 text-base text-muted-foreground md:text-lg">{t("compat.sub")}</p>

      <div className="mt-8 grid gap-3 rounded-2xl border border-border bg-card p-5 sm:grid-cols-2">
        <div>
          <label className="mb-1.5 block text-xs font-medium text-muted-foreground">{t("compat.first")}</label>
          <Select value={a} onValueChange={setA}>
            <SelectTrigger data-testid="compat-select-a"><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent>
              {types.map((x) => <SelectItem key={x.key} value={x.key}>{pick(x, "name")}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="mb-1.5 block text-xs font-medium text-muted-foreground">{t("compat.second")}</label>
          <Select value={b} onValueChange={setB}>
            <SelectTrigger data-testid="compat-select-b"><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent>
              {types.map((x) => <SelectItem key={x.key} value={x.key}>{pick(x, "name")}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="sm:col-span-2">
          <Button onClick={compare} disabled={!a || !b || busy} className="w-full rounded-full" data-testid="compat-compare">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <HeartHandshake className="mr-2 h-4 w-4" />}
            {t("compat.compare")}
          </Button>
        </div>
      </div>

      {!result && (
        <p className="mt-8 text-center text-sm text-muted-foreground" data-testid="compat-empty">{t("compat.empty")}</p>
      )}

      {result && (
        <div className="mt-8 rounded-3xl border border-border bg-card p-7" data-testid="compat-result">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <TypeChip t={result.a} />
              <span className="text-muted-foreground">×</span>
              <TypeChip t={result.b} />
            </div>
            <div className="flex h-20 w-20 flex-col items-center justify-center rounded-full border-4 border-primary text-center" data-testid="compat-score">
              <span className="font-display text-2xl font-semibold text-primary">{result.score}%</span>
              <span className="text-[9px] uppercase tracking-wide text-muted-foreground">{t("compat.score")}</span>
            </div>
          </div>
          <p className="mt-4 text-sm text-muted-foreground">
            {pick(result.a, "tagline")} · {pick(result.b, "tagline")}
          </p>

          {result.aligned.length > 0 && (
            <div className="mt-6" data-testid="compat-aligned">
              <h3 className="text-sm font-semibold text-primary">{t("compat.aligned")}</h3>
              <div className="mt-2 flex flex-wrap gap-2">
                {result.aligned.map((s) => (
                  <span key={s} className="rounded-full bg-secondary px-3 py-1 text-xs font-medium text-secondary-foreground">{s}</span>
                ))}
              </div>
            </div>
          )}
          {result.contrast.length > 0 && (
            <div className="mt-5" data-testid="compat-contrast">
              <h3 className="text-sm font-semibold text-amber-600">{t("compat.contrast")}</h3>
              <div className="mt-2 flex flex-wrap gap-2">
                {result.contrast.map((s) => (
                  <span key={s} className="rounded-full bg-amber-50 px-3 py-1 text-xs font-medium text-amber-700">{s}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
